import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import { FaChartLine, FaChartBar, FaChartPie, FaClock } from 'react-icons/fa';
import axios from 'axios';
import './Analytics.css';

const API_BASE_URL = 'http://localhost:3001/api/study-tools';

const Analytics = () => {
  const [timeData, setTimeData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [productivityData, setProductivityData] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('week');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const COLORS = ['#2ecc71', '#3498db', '#e74c3c', '#f1c40f', '#9b59b6'];

  useEffect(() => {
    fetchAnalytics();
  }, [selectedTimeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/analytics`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { timeRange: selectedTimeRange }
      });

      const { studySessions, subjectDistribution } = response.data;

      // Process time tracking data
      const timeTracking = studySessions.map(session => ({
        date: new Date(session.date).toLocaleDateString(),
        hours: session.duration / 60 // Convert minutes to hours
      }));

      // Process subject distribution data
      const subjects = subjectDistribution.map(subject => ({
        name: subject.name,
        hours: subject.hours
      }));

      // Process productivity scores
      const productivity = studySessions.map(session => ({
        date: new Date(session.date).toLocaleDateString(),
        score: session.productivityScore || 0
      }));

      setTimeData(timeTracking);
      setSubjectData(subjects);
      setProductivityData(productivity);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalHours = () => {
    return timeData.reduce((total, day) => total + day.hours, 0).toFixed(1);
  };

  const calculateAverageProductivity = () => {
    if (productivityData.length === 0) return 0;
    return Math.round(
      productivityData.reduce((total, day) => total + day.score, 0) / productivityData.length
    );
  };

  const getMostStudiedSubject = () => {
    if (subjectData.length === 0) return "N/A";
    return subjectData.reduce((prev, current) =>
      prev.hours > current.hours ? prev : current
    ).name;
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-spinner">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="error-message">
          {error}
          <button onClick={fetchAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>Study Analytics</h2>
        <div className="time-range-selector">
          <button 
            className={selectedTimeRange === 'week' ? 'active' : ''} 
            onClick={() => setSelectedTimeRange('week')}
          >
            Week
          </button>
          <button 
            className={selectedTimeRange === 'month' ? 'active' : ''} 
            onClick={() => setSelectedTimeRange('month')}
          >
            Month
          </button>
        </div>
      </div>

      <div className="analytics-summary">
        <div className="summary-card">
          <FaClock className="summary-icon" />
          <div className="summary-info">
            <h3>Total Study Time</h3>
            <div className="summary-value">{calculateTotalHours()} hours</div>
          </div>
        </div>
        <div className="summary-card">
          <FaChartLine className="summary-icon" />
          <div className="summary-info">
            <h3>Average Productivity</h3>
            <div className="summary-value">{calculateAverageProductivity()}%</div>
          </div>
        </div>
        <div className="summary-card">
          <FaChartPie className="summary-icon" />
          <div className="summary-info">
            <h3>Most Studied</h3>
            <div className="summary-value">{getMostStudiedSubject()}</div>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-container">
          <h3><FaChartLine /> Study Time Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="hours" 
                stroke="#2ecc71" 
                name="Hours Studied"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3><FaChartPie /> Subject Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={subjectData}
                dataKey="hours"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {subjectData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3><FaChartBar /> Productivity Score</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={productivityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar 
                dataKey="score" 
                fill="#3498db" 
                name="Productivity Score"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* <div className="analytics-insights">
        <h3>Key Insights</h3>
        <div className="insights-list">
          {timeData.length > 0 ? (
            <>
              <div className="insight-item">
                <strong>Peak Performance:</strong> Your most productive days are typically mid-week
              </div>
              <div className="insight-item">
                <strong>Study Pattern:</strong> You tend to study more effectively in longer sessions
              </div>
              <div className="insight-item">
                <strong>Subject Balance:</strong> Consider allocating more time to less-studied subjects
              </div>
            </>
          ) : (
            <div className="no-data-message">
              Start tracking your study sessions to see insights about your study patterns.
            </div>
          )}
        </div>
      </div> */}
    </div>
  );
};

export default Analytics; 